import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '../../stores/useAuthStore';
import { LoadingSpinner } from '../LoadingSpinner';

interface ConfirmPasswordResetFormProps {
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ConfirmPasswordResetForm: React.FC<ConfirmPasswordResetFormProps> = ({ email, onSuccess, onCancel }) => {
  const { confirmPasswordReset, error, isValidating } = useAuthStore();
  const [formData, setFormData] = useState({
    code: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);

    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    
    try {
      await confirmPasswordReset(email, formData.code, formData.password);
      onSuccess();
    } catch (err) {
      // Error handled in store
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in">
      <div className="text-center mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nueva contraseña</h3>
        <p className="text-sm text-gray-500 mt-2">Hemos enviado un código a <strong>{email}</strong></p>
      </div>

       <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Código de verificación
        </label>
        <div className="relative">
             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
            </div>
          <input
            type="text"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors tracking-widest text-center font-mono text-lg"
            placeholder="000000"
            maxLength={6}
            required
            disabled={isValidating}
          />
        </div>
      </div>

      <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Nueva contraseña
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors"
              placeholder="••••••••"
              required
              minLength={8}
              disabled={isValidating}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Confirmar nueva contraseña
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="block w-full pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-colors"
              placeholder="••••••••"
              required
              minLength={8}
              disabled={isValidating}
            />
          </div>
        </div>

      {(error || passwordError) && (
        <div className="text-red-500 text-sm text-center bg-red-50 dark:bg-red-900/20 p-2 rounded-lg">
          {error || passwordError}
        </div>
      )}

      <div className="pt-2 flex flex-col gap-3">
        <button
          type="submit"
          disabled={isValidating}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isValidating ? <LoadingSpinner size="sm" color="white" /> : 'Confirmar cambio'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={isValidating}
          className="w-full flex justify-center items-center py-2 px-4 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </button>
      </div>
    </form>
  );
};
